/** Shared labels / options for wholesaler purchase orders (English). */

export const PAYMENT_MODELS = {
  deposit: {
    label: "Deposit + balance",
    short: "Deposit",
    desc: "Deposit on order; balance due once goods are ready at the wholesaler.",
    pill: "amber",
    sub: (o) => `${o.deposit_pct ?? 10} % deposit`,
  },
  prepaid: {
    label: "Prepaid 100 %",
    short: "Prepaid",
    desc: "Pay in full before the wholesaler ships.",
    pill: "blue",
    sub: () => "before shipment",
  },
  partial_shipment: {
    label: "Paid in full · partial shipments",
    short: "Full pay · splits",
    desc: "Pay everything upfront; goods arrive in multiple shipments.",
    pill: "purple",
    sub: () => "paid in full, goods in parts",
  },
  preorder: {
    label: "Pre-order",
    short: "Pre-order",
    desc: "No deposit. Pay when the wholesaler confirms availability.",
    pill: "teal",
    sub: (o) => (o.goods_ready ? "goods available" : "no deposit yet"),
  },
  net_terms: {
    label: "Payment terms",
    short: "Net terms",
    desc: "Goods arrive first; pay X days after goods receipt.",
    pill: "grey",
    sub: (o) => `${o.payment_term_days || 14} days after receipt`,
  },
};

export const PHASES = {
  payment_due: { label: "Payment due", pill: "red" },
  in_transit: { label: "In transit", pill: "blue" },
  partially_delivered: { label: "Partially delivered", pill: "purple" },
  waiting_for_goods: { label: "Waiting for goods", pill: "grey" },
  completed: { label: "Completed", pill: "green" },
};

/** Pill tones matching the HTML preview (works on dark card backgrounds). */
export const PILL_SX = {
  blue: { bg: "#16285a", color: "#a9c3ff", border: "#2a4597" },
  amber: { bg: "#3d2a0a", color: "#ffc56b", border: "#6b4a12" },
  purple: { bg: "#2a1f55", color: "#cbbcff", border: "#4a3a92" },
  teal: { bg: "#0f3540", color: "#86e0f2", border: "#1d5d6e" },
  grey: { bg: "#222d47", color: "#c6d0e6", border: "#36446a" },
  red: { bg: "#45161b", color: "#ffaaa2", border: "#7a2a31" },
  green: { bg: "#0f3a2a", color: "#86e5b9", border: "#1c6b4c" },
};

export const BAR_COLORS = {
  blue: "#4b84f5",
  green: "#22b077",
  amber: "#f0a92b",
};

export const DOT_COLORS = {
  blue: "#4b84f5",
  green: "#22b077",
  amber: "#f0a92b",
  red: "#ef6a5d",
  grey: "#7d8bab",
};

export const SEGMENTS = [
  { key: "open", label: "Open" },
  { key: "due", label: "Payment due" },
  { key: "prepaid", label: "Prepaid outstanding" },
  { key: "ship", label: "In delivery" },
  { key: "done", label: "Completed" },
  { key: "all", label: "All" },
];

export const PAYMENT_KINDS = [
  { value: "deposit", label: "Deposit" },
  { value: "balance", label: "Balance" },
  { value: "partial", label: "Partial payment" },
  { value: "full", label: "Full payment" },
];

export const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "wise", label: "Wise" },
  { value: "credit_card", label: "Credit card" },
  { value: "paypal", label: "PayPal" },
];

export const CARRIERS = [
  { value: "dhl", label: "DHL" },
  { value: "ups", label: "UPS" },
  { value: "gls", label: "GLS" },
  { value: "dpd", label: "DPD" },
  { value: "dsv", label: "DSV" },
  { value: "dachser", label: "Dachser" },
  { value: "hermes", label: "Hermes" },
  { value: "fedex", label: "FedEx" },
  { value: "freight", label: "Freight" },
  { value: "pickup", label: "Pickup" },
  { value: "zufall", label: "Zufall" },
  { value: "other", label: "Other" },
];

export const SHIP_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "in_transit", label: "In transit" },
  { value: "delivered", label: "Delivered" },
  { value: "problem", label: "Problem" },
];

export const DELIVER_TO = [
  { value: "prep_center", label: "Prep center" },
  { value: "own_warehouse", label: "Own warehouse" },
  { value: "amazon_fba", label: "Amazon FBA direct" },
];

export const SHIP_METHODS = [
  { value: "parcel", label: "Parcel" },
  { value: "freight", label: "Freight / pallet" },
  { value: "pickup", label: "Pickup" },
];

export const userDisplayName = (u) =>
  (u?.full_name || u?.username || u?.email || "").trim();

/** Live payment-plan preview rows for the create-order dialog. */
export const buildPlanPreview = (form) => {
  const total = Number(form.total_net) || 0;
  const money = (n) =>
    n.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €";
  const payBy = form.pay_by
    ? `by ${String(form.pay_by).slice(8, 10)}.${String(form.pay_by).slice(
        5,
        7,
      )}.${String(form.pay_by).slice(0, 4)}`
    : null;
  const k = form.payment_model;
  if (k === "deposit") {
    const p = Number(form.deposit_pct) || 10;
    const dep = Math.round(total * p) / 100;
    return [
      [`Deposit ${p} %`, payBy || "on order", money(dep)],
      [
        `Balance ${100 - p} %`,
        "when goods ready at wholesaler",
        money(total - dep),
      ],
    ];
  }
  if (k === "prepaid") {
    return [["Prepaid 100 %", payBy || "before shipment", money(total)]];
  }
  if (k === "partial_shipment") {
    return [
      ["Full payment 100 %", payBy || "immediately", money(total)],
      [
        `${form.expected_parts || "?"} partial shipments`,
        "goods arrive over time",
        "",
      ],
    ];
  }
  if (k === "preorder") {
    const e = form.expected_availability;
    return [
      ["Deposit", "none", money(0)],
      ["Payment 100 %", "when available" + (e ? ` (~${e})` : ""), money(total)],
    ];
  }
  if (k === "net_terms") {
    return [
      [
        "Payment 100 %",
        `${form.payment_term_days || "?"} days after goods receipt`,
        money(total),
      ],
    ];
  }
  return [];
};

export const formatMoney = (n, currency = "EUR") => {
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  return (
    v.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) +
    " " +
    (currency === "EUR" ? "€" : currency)
  );
};

export const formatMoneyShort = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  return Math.round(v).toLocaleString("de-DE") + " €";
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  const d = String(iso).slice(0, 10);
  const [y, m, day] = d.split("-");
  if (!y || !m || !day) return d;
  return `${day}.${m}.${y}`;
};

export const dueRelative = (payBy, today = new Date()) => {
  if (!payBy) return null;
  const t0 = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const parts = String(payBy).slice(0, 10).split("-").map(Number);
  const t1 = Date.UTC(parts[0], parts[1] - 1, parts[2]);
  const n = Math.round((t1 - t0) / 86400000);
  const rel =
    n < 0
      ? `overdue by ${-n} day${-n === 1 ? "" : "s"}`
      : n === 0
        ? "due today"
        : n === 1
          ? "due tomorrow"
          : `in ${n} days`;
  return {
    text: `due ${formatDate(payBy)} · ${rel}`,
    hot: n <= 2,
  };
};
